import { defineStore } from 'pinia'
import { ref } from 'vue'
import type { FairSearchV2ProductCard } from '@/widgets/search/fair-v2/fair-search-v2.data'
import type { GiftQuizMode } from '../gift-quiz.data'

/**
 * Gift quiz — open / close, and the last set of ideas for the Gift ideas page. There is no
 * saved "gift set" on the server (BE-153), so the page reads the set from the session.
 */
const RESULT_KEY = 'fair.gift.result'

export interface GiftQuizResult {
  recipientName: string
  chips: string[]
  cards: FairSearchV2ProductCard[]
}

export type { GiftQuizMode }

/**
 * How the last run ended on Save (T-135): `added` — Add person, `tuned` — Tune only. The
 * quiz shows the toast itself; callers (People, the search selector) can read the id.
 * `null` — closed any other way (a person created on Who stays, no toast, R288).
 */
export interface GiftQuizOutcome {
  kind: 'added' | 'tuned'
  personId: number
  name: string
}

function readResult(): GiftQuizResult | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.sessionStorage.getItem(RESULT_KEY)
    return raw ? (JSON.parse(raw) as GiftQuizResult) : null
  } catch {
    return null
  }
}

export const useGiftQuizStore = defineStore('giftQuiz', () => {
  const isOpen = ref(false)
  /** Bumped on every open so the quiz re-reads People. */
  const session = ref(0)
  /** People id chosen before the quiz (the hub's person chip). */
  const presetPersonId = ref<number | null>(null)
  const mode = ref<GiftQuizMode>('gift')
  const result = ref<GiftQuizResult | null>(readResult())
  const outcome = ref<GiftQuizOutcome | null>(null)

  /**
   * `gift` — Find a gift (with a person: starts at Occasion); `add-person` — People ›
   * Add person (T-134, R288); `tune` — Add / Update taste for `personId` (T-135).
   */
  function open(personId: number | null = null, openMode: GiftQuizMode = 'gift') {
    presetPersonId.value = personId
    mode.value = openMode
    outcome.value = null
    session.value += 1
    isOpen.value = true
  }

  function close() {
    isOpen.value = false
  }

  /** Save on the last step of Add person / Tune only (T-135). */
  function finish(value: GiftQuizOutcome) {
    outcome.value = value
    close()
  }

  function setResult(value: GiftQuizResult) {
    result.value = value
    if (typeof window === 'undefined') return
    try {
      window.sessionStorage.setItem(RESULT_KEY, JSON.stringify(value))
    } catch {
      // Quota — the page still has it in memory until a reload.
    }
  }

  /** People › Add person (T-134). */
  const openAddPerson = () => open(null, 'add-person')
  /** Add taste / Update taste — the taste steps only (T-135). */
  const openTune = (personId: number) => open(personId, 'tune')

  return {
    isOpen,
    session,
    presetPersonId,
    mode,
    result,
    outcome,
    open,
    openAddPerson,
    openTune,
    close,
    finish,
    setResult,
  }
})
